import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, of, delay } from 'rxjs';
import { BpmnModelerService } from '../services/bpmn-modeler.service';
import { BpmnLintResult, BpmnLintIssue } from '../services/bpmn-lint.service';
import {
  BpmnService,
  BpmnProcessDetails,
} from '../bpmn-list/services/bpmn.service';
import { BpmnItem } from '../bpmn-list/bpmn.config';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';

@Component({
  selector: 'app-bpmn-view',
  standalone: true,
  imports: [CommonModule, ButtonModule, ToastModule, TranslateModule, AppButtonComponent],
  templateUrl: './bpmn-view.component.html',
  styleUrl: './bpmn-view.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BpmnViewComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly translateService = inject(TranslateService);
  private readonly bpmnModelerService = inject(BpmnModelerService);
  private readonly bpmnService = inject(BpmnService);
  private readonly messageService = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  @ViewChild('modelerContainer', { static: false })
  modelerContainer!: ElementRef;

  @ViewChild('propertiesPanelContainer', { static: false })
  propertiesPanelContainer!: ElementRef;

  // Signals for component state
  loading = signal(false);
  processData = signal<BpmnItem | null>(null);
  lintResults = signal<BpmnLintResult>({
    issues: [],
    hasErrors: false,
    hasWarnings: false,
    hasInfo: false,
  });

  // Computed properties
  hasLintErrors = computed(() => {
    return this.lintResults().hasErrors;
  });

  hasLintWarnings = computed(() => {
    return this.lintResults().hasWarnings;
  });

  hasLintIssues = computed(() => {
    const results = this.lintResults();
    return results.hasErrors || results.hasWarnings || results.hasInfo;
  });

  lintErrorCount = computed(() => {
    return this.lintResults().issues.filter(
      (issue) => issue.severity === 'error'
    ).length;
  });

  lintWarningCount = computed(() => {
    return this.lintResults().issues.filter(
      (issue) => issue.severity === 'warn'
    ).length;
  });

  lintInfoCount = computed(() => {
    return this.lintResults().issues.filter(
      (issue) => issue.severity === 'info'
    ).length;
  });

  ngOnInit(): void {
    const processId = this.route.snapshot.paramMap.get('id');
    if (processId) {
      this.loadProcessData(processId);
    } else {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'bpmn.view.messages.error.title'
        ),
        detail: this.translateService.instant(
          'bpmn.view.messages.error.invalid_id'
        ),
      });
    }
  }

  ngAfterViewInit(): void {
    // Initialize BPMN modeler after view is ready
    this.initializeBpmnModeler();
    this.setupLintingListener();
  }

  /**
   * Load process data by ID
   */
  private loadProcessData(processId: string): void {
    this.loading.set(true);

    // Get process data and XML content in a single API call
    this.bpmnService.getBpmnProcessWithXml(processId).subscribe({
      next: (details) => {
        if (details && details.process) {
          this.processData.set(details.process);

          // Load BPMN XML content if available
          if (details.xmlContent) {
            this.importBpmnXml(details.xmlContent);
          } else {
            // This should not happen anymore as we provide fallback XML
            console.warn('No XML content available for process');
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant(
                'bpmn.view.messages.error.title'
              ),
              detail: this.translateService.instant(
                'bpmn.view.messages.error.no_content_found'
              ),
            });
          }
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'bpmn.view.messages.error.title'
            ),
            detail: this.translateService.instant(
              'bpmn.view.messages.error.process_not_found'
            ),
          });
        }
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Failed to load process data:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant(
            'bpmn.view.messages.error.title'
          ),
          detail: this.translateService.instant(
            'bpmn.view.messages.error.failed_to_load'
          ),
        });
        this.loading.set(false);
      },
    });
  }

  /**
   * Import BPMN XML content into the modeler
   */
  private async importBpmnXml(xmlContent: string): Promise<void> {
    try {
      await this.bpmnModelerService.importXml(xmlContent);
    } catch (error) {
      console.error('Failed to import BPMN XML:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'bpmn.view.messages.error.title'
        ),
        detail: this.translateService.instant(
          'bpmn.view.messages.error.failed_to_load_content'
        ),
      });
    }
  }

  /**
   * Initialize BPMN modeler
   */
  private async initializeBpmnModeler(): Promise<void> {
    try {
      if (
        this.modelerContainer?.nativeElement &&
        this.propertiesPanelContainer?.nativeElement
      ) {
        await this.bpmnModelerService.initialize({
          container: this.modelerContainer.nativeElement,
          propertiesPanel: this.propertiesPanelContainer.nativeElement,
          width: '100%',
          height: '100%',
        });
      }
    } catch (error) {
      console.error('Failed to initialize BPMN modeler:', error);
    }
  }

  /**
   * Setup listener for BPMN linting results
   */
  private setupLintingListener(): void {
    this.bpmnModelerService
      .getLintResults$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((results: BpmnLintResult) => {
        this.lintResults.set(results);
        this.cdr.markForCheck();
      });
  }

  /**
   * Zoom in the BPMN canvas
   */
  onZoomIn(): void {
    this.bpmnModelerService.zoomIn();
  }

  /**
   * Zoom out the BPMN canvas
   */
  onZoomOut(): void {
    this.bpmnModelerService.zoomOut();
  }

  /**
   * Fit the BPMN diagram to viewport
   */
  onFitToViewport(): void {
    this.bpmnModelerService.fitToViewport();
  }

  /**
   * Reset zoom to 100%
   */
  onResetZoom(): void {
    this.bpmnModelerService.resetZoom();
  }

  /**
   * Export BPMN as file
   */
  async exportBpmnAsFile(): Promise<void> {
    try {
      const result = await this.bpmnModelerService.exportXml();

      if (result.error) {
        console.error('Export failed:', result.error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant(
            'bpmn.view.messages.error.title'
          ),
          detail: this.translateService.instant(
            'bpmn.view.messages.error.export_failed'
          ),
        });
        return;
      }

      const processData = this.processData();
      // Use fileName from API response, fallback to name if not available
      const fileName =
        processData?.fileName || processData?.name || 'bpmn-process';
      const blob = new Blob([result.xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant(
          'bpmn.view.messages.export_success'
        ),
        detail: this.translateService.instant(
          'bpmn.view.messages.export_file_success'
        ),
      });
    } catch (error) {
      console.error('Failed to export BPMN file:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'bpmn.view.messages.error.title'
        ),
        detail: this.translateService.instant(
          'bpmn.view.messages.error.export_failed'
        ),
      });
    }
  }

  /**
   * Export BPMN as image
   */
  async exportBpmnAsImage(): Promise<void> {
    try {
      const result = await this.bpmnModelerService.exportSvg();

      if (result.error) {
        console.error('SVG export failed:', result.error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant(
            'bpmn.view.messages.error.title'
          ),
          detail: this.translateService.instant(
            'bpmn.view.messages.error.export_failed'
          ),
        });
        return;
      }

      const processData = this.processData();
      // Use fileName from API response, fallback to name if not available
      const baseFileName =
        processData?.fileName || processData?.name || 'bpmn-process';
      // Remove .bpmn extension if present and add .svg
      const fileName = baseFileName.replace(/\.bpmn$/, '') + '.svg';
      const blob = new Blob([result.svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant(
          'bpmn.view.messages.export_success'
        ),
        detail: this.translateService.instant(
          'bpmn.view.messages.export_image_success'
        ),
      });
    } catch (error) {
      console.error('Failed to export BPMN image:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'bpmn.view.messages.error.title'
        ),
        detail: this.translateService.instant(
          'bpmn.view.messages.error.export_failed'
        ),
      });
    }
  }

  /**
   * Handle Export File button click
   */
  onExportFile(): void {
    this.exportBpmnAsFile();
  }

  /**
   * Handle Export Image button click
   */
  onExportImage(): void {
    this.exportBpmnAsImage();
  }

  /**
   * Handle Back button click
   */
  onBack(): void {
    this.router.navigate(['/configuration/master-data/bpmn']);
  }

  ngOnDestroy(): void {
    this.bpmnModelerService.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
